import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Hand, Loader2, Monitor, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { botsApi } from '../../../services/api';
import { useWorkspaceQuery } from '../../../hooks/queries';
import { useDecideApproval, useWorkspaceAction } from '../../../hooks/mutations';
import { Screen } from '../ComputerView';
import { CardShell, ResolvedCard, ghostBtn, inputCls, outcomeOf, placeOf, primaryBtn } from './CardShell';

const useShot = (shotId) => useQuery({
  queryKey: ['bot-shot', shotId],
  queryFn: () => botsApi.shotBlob(shotId).then((r) => URL.createObjectURL(r.data)),
  enabled: !!shotId,
  staleTime: Infinity,
  retry: false,
});

/**
 * The bot needs a person on its computer (a login, a captcha, 2FA), like Grok
 * Bot's "take over": the screen right in the chat, one click to take control,
 * and handing it back (with a note) lets the bot continue.
 *
 * `docked`: the computer panel (ComputerView) is open next to or above the chat. The
 * screen and handing back are there then, so the card does not show them a second time.
 */
const TakeoverCard = ({ approval, botName, compact, onOpenComputer, docked }) => {
  const { t } = useTranslation();
  const card = approval.card;
  const projectId = placeOf(approval);
  const decide = useDecideApproval();
  const act = useWorkspaceAction();
  const pending = approval.status === 'pending';
  const { data } = useWorkspaceQuery(projectId, { enabled: pending && !!projectId, refetchInterval: pending ? 5000 : false });
  const shot = useShot(pending ? card.shot_id : null);
  const [note, setNote] = useState('');

  if (!pending) {
    return (
      <ResolvedCard icon={Monitor} testid="takeover-card-resolved" ok={approval.status === 'approved'}
        text={approval.note ? `${card.reason} · ${approval.note}` : card.reason}
        outcome={outcomeOf(approval, t, t('bots.cards.handed_back'))} />
    );
  }

  const ws = data?.workspace;
  const live = ws?.status === 'running' && ws.preview_url;
  const mine = live && ws.lease?.mine;
  const busy = act.isPending || decide.isPending;
  const onErr = (err) => toast.error(err?.response?.data?.detail || t('bots.desktop.failed'));
  const takeOver = () => act.mutate({ projectId, action: 'takeOver' }, { onError: onErr, onSuccess: () => onOpenComputer?.() });
  const giveBack = () => act.mutate({ projectId, action: 'giveBack', note }, { onError: onErr, onSuccess: () => setNote('') });

  return (
    <CardShell icon={Hand} testid="takeover-card" waiting compact={compact}
      eyebrow={t('bots.cards.takeover_eyebrow', { bot: botName })} title={card.reason}
      footer={mine && docked ? (
        <span className="text-xs text-amber-700 dark:text-amber-300" data-testid="takeover-in-panel">
          {t('bots.desktop.in_control', { bot: botName })}
        </span>
      ) : mine ? (
        <form className="flex flex-1 flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); giveBack(); }}>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
            placeholder={t('bots.desktop.note_placeholder')} className={`${inputCls} flex-1 min-w-[160px]`} data-testid="takeover-note" />
          <button type="submit" className={primaryBtn} disabled={busy} data-testid="takeover-give-back">
            <Undo2 className="w-3.5 h-3.5" />{t('bots.desktop.hand_back')}
          </button>
        </form>
      ) : (
        <>
          <span className="flex-1" />
          <button type="button" className={ghostBtn} disabled={busy} data-testid="card-decline"
            onClick={() => decide.mutate({ approvalId: approval.id, approve: false })}>
            {t('bots.cards.continue_without')}
          </button>
          <button type="button" className={primaryBtn} disabled={busy || !projectId} onClick={takeOver} data-testid="takeover-take">
            <Hand className="w-3.5 h-3.5" />{t('bots.desktop.take_control')}
          </button>
        </>
      )}>
      {!docked && (
      <div>
        {/* A compact card (Home's "Needs you") shows only a strip of the last picture: the
            live screen belongs to the chat, where taking control opens it. */}
        {compact ? (shot.data && (
          <img src={shot.data} alt={card.reason} className="w-full h-24 object-cover object-top rounded-xl ring-1 ring-border" />
        )) : live ? (
          <Screen ws={ws} size="card" busy={busy} t={t} onTakeOver={takeOver} />
        ) : shot.data ? (
          <img src={shot.data} alt={card.reason} className="w-full rounded-xl ring-1 ring-border" />
        ) : (
          <div className="h-40 rounded-xl bg-muted/50 ring-1 ring-border flex items-center justify-center text-xs text-muted-foreground gap-2">
            {shot.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Monitor className="w-4 h-4" />}
            {t('bots.desktop.computer_title')}
          </div>
        )}
      </div>
      )}
    </CardShell>
  );
};

export default TakeoverCard;
